import ZoomControl from 'components/Map/PixiMap/ZoomControl';
import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import L from 'leaflet';
import {
  ImageOverlay,
  Map as LeafletMap,
  Pane,
} from 'react-leaflet';
import { connect } from 'react-redux';

import DistrictLabelLayer from 'components/Map/DistrictLabelLayer';
import useHandleAreaClick from 'components/Map/PixiMap/useHandleAreaClick';
import useHandleHitboxTap from 'components/Map/PixiMap/useHandleHitboxTap';
import useZoomToDistrict from 'components/Map/PixiMap/useZoomToDistrict';
import ReactLeafletPixiOverlay from 'components/Map/ReactLeafletPixiOverlay';
import {
  getMapDimensionsForSetting,
  getMinimumZoomThatFits,
  xy,
} from 'mapping';
import { ModalTooltip } from 'components/ModalTooltip/ModalTooltipContainer';
import { ITooltipData } from 'components/ModalTooltip/types';
import { MAP_BASE_URL } from 'mapping/constants';
import getCRSForSetting from 'mapping/getCRSForSetting';
import getIdealMinimumZoomForSetting from 'mapping/getIdealMinimumZoomForSetting';
import getMapZoomLimitsForSetting from 'mapping/getMapZoomLimitsForSetting';
import getMinimumZoomLevelForDestinations from 'mapping/getMinimumZoomLevelForDestinations';
import getLabelledStateAwareAreas from 'selectors/map/getLabelledStateAwareAreas';
import { IAppState } from 'types/app';
import { IMappableSetting } from 'types/map';
import { BaseProps } from './props';

import useHandleZoomEnd from './useHandleZoomEnd';
import PlayerMarkers from '../PlayerMarkers';

const SAFE_AREA_PADDING = 0;

export function UnterzeePixiMap({
  areas,
  currentArea,
  initialCenter,
  initialZoom,
  isModalTooltipOpen,
  onAreaClick,
  onAreaSelect,
  onClick,
  onMoveEnd,
  onZoomEnd,
  selectedArea,
  setIsModalTooltipOpen,
  setTooltipData,
  setting,
  tooltipData,
}: Props) {
  const mapRef = useRef<any>(null);
  const [zoomLevel, setZoomLevel] = useState(initialZoom);

  const minimumZoomLevelForDestinations = useMemo(() => {
    if (!setting?.mapRootArea?.areaKey) {
      return 0;
    }
    return getMinimumZoomLevelForDestinations(setting as IMappableSetting);
  }, [setting]);

  const { height: mapHeight, width: mapWidth } = useMemo(() => {
    if (!setting?.mapRootArea?.areaKey) {
      return { height: 0, width: 0 };
    }
    return getMapDimensionsForSetting(setting as IMappableSetting);
  }, [setting]);

  const maxZoom = useMemo(() => {
    if (!setting?.mapRootArea?.areaKey) {
      return 0;
    }
    return getMapZoomLimitsForSetting(setting as IMappableSetting).max;
  }, [setting]);

  const minZoom = useMemo(
    () => {
      if (!setting?.mapRootArea?.areaKey) {
        return 0;
      }
      return Math.max(
        getIdealMinimumZoomForSetting(setting as IMappableSetting),
        getMinimumZoomThatFits(window, setting as IMappableSetting),
      );
    },
    [setting],
  );

  const handleRequestCloseModalTooltip = useCallback(() => {
    setIsModalTooltipOpen(false);
    onAreaSelect();
  }, [onAreaSelect, setIsModalTooltipOpen]);

  const handleZoomEnd = useHandleZoomEnd(
    minimumZoomLevelForDestinations,
    onAreaSelect,
    onZoomEnd,
    selectedArea,
    setZoomLevel,
  );

  const zoomToDistrict = useZoomToDistrict(mapRef);

  const handleAreaClick = useHandleAreaClick(
    minimumZoomLevelForDestinations,
    onAreaClick,
    zoomLevel,
    zoomToDistrict,
  );

  const handleHitboxTap = useHandleHitboxTap(
    currentArea,
    minimumZoomLevelForDestinations,
    onAreaClick,
    onAreaSelect,
    setIsModalTooltipOpen,
    setTooltipData,
    zoomLevel,
    zoomToDistrict,
  );

  if (!setting?.mapRootArea?.areaKey) {
    return null;
  }

  // noinspection PointlessArithmeticExpressionJS
  return (
    <LeafletMap
      ref={mapRef}
      attributionControl={false}
      maxBounds={[
        [0 - SAFE_AREA_PADDING, 0 + SAFE_AREA_PADDING],
        [-mapHeight + SAFE_AREA_PADDING, mapWidth - SAFE_AREA_PADDING],
      ]}
      bounceAtZoomLimits={false}
      className="leaflet-container--unterzee"
      maxBoundsViscosity={1}
      center={xy(initialCenter[0], initialCenter[1])}
      crs={getCRSForSetting(setting as IMappableSetting)}
      zoom={zoomLevel}
      minZoom={minZoom}
      maxZoom={maxZoom}
      zoomDelta={0.5}
      zoomSnap={0.5}
      onClick={onClick}
      onmoveend={onMoveEnd}
      onzoomend={handleZoomEnd}
      tms={false}
      zoomControl={false}
    >
      <Pane
        style={{ zIndex: 99 }} // Hide this pane if we can show basically anything else
      >
        <ImageOverlay
          url={`${MAP_BASE_URL}/fallback/unterzee-background.png`}
          bounds={L.latLngBounds(
            xy(0, 0),
            xy(mapWidth, -mapHeight),
          )}
        />
      </Pane>

      <ZoomControl
        setZoomLevel={setZoomLevel}
        zoomLevel={zoomLevel}
        zoomDelta={0.3}
      />

      <ReactLeafletPixiOverlay
        // This is a hack to work around the fact that TS doesn't recognise that ReactLeafletPixiOverlay
        // can take `selectedArea` as a prop; it's not an excess property but relates to this issue:
        // https://github.com/Microsoft/TypeScript/issues/15463
        // {...{ selectedArea }}
        // @ts-ignore
        selectedArea={selectedArea}
      />

      <DistrictLabelLayer
        areas={areas}
        currentArea={currentArea}
        onAreaClick={handleAreaClick}
        onAreaSelect={onAreaSelect}
        onTapAtLowZoomLevel={handleHitboxTap}
        tooltipClassName="leaflet-tooltip--fbg__name--unterzee-landmark"
        zoomLevel={zoomLevel}
      />

      <PlayerMarkers />

      <ModalTooltip
        modalIsOpen={isModalTooltipOpen}
        onRequestClose={handleRequestCloseModalTooltip}
        tooltipData={tooltipData}
        disableTouchEvents
      />
    </LeafletMap>
  );
}

const mapStateToProps = (state: IAppState) => ({
  areas: getLabelledStateAwareAreas(state),
  setting: state.map.setting,
});

type Props = BaseProps & ReturnType<typeof mapStateToProps> & {
  isModalTooltipOpen: boolean,
  setIsModalTooltipOpen: (isOpen: boolean) => void,
  setTooltipData: (tooltipData: ITooltipData) => void,
  tooltipData: ITooltipData,
};


export default connect(mapStateToProps)(UnterzeePixiMap);